import { invariant } from './invariant'

/**
 * Factory class for creating and managing track commands.
 */
export class TrackEventCommandFactory {
  /**
   * Map to store track commands.
   * @private
   * @type {Map<import("./event-type").EventType, Function>}
   */
  _commands = new Map()

  /**
   * Checks if a command is registered for a given event type.
   * @param {import("./event-type").EventType} eventType - The event type to check.
   * @returns {boolean} True if a command is registered for the event type, false otherwise.
   */
  hasCommand(eventType) {
    return this._commands.has(eventType)
  }

  /**
   * Registers a new track command.
   * @param {import("./event-type").EventType} eventType - The event type to register the command for.
   * @param {(tracker: import('./event-tracker').EventTracker, event: Record<string, string | number | boolean>) => void} command - The command function to be executed for the event.
   * @throws {Error} If the event type is already registered.
   */
  register(eventType, command) {
    invariant(
      !this.hasCommand(eventType),
      `Command for event type '${eventType}' is already registered.`,
    )

    this._commands.set(eventType, command)
  }

  /**
   * Retrieves a track command for a given event type.
   * @param {import("./event-type").EventType} eventType - The event type to retrieve the command for.
   * @returns {(tracker: import('./event-tracker').EventTracker, event: Record<string, string | number | boolean>) => void} The command function for the event type.
   * @throws {Error} If no command is registered for the event type.
   */
  retrieve(eventType) {
    invariant(
      this.hasCommand(eventType),
      `No command registered for event type '${eventType}'.`,
    )

    return this._commands.get(eventType)
  }
}
